import { InvoiceStatus } from '../enums/invoice-status.enum';

/**
 * PD-15161 — the invoice open/close lifecycle.
 *
 * `invoice.status` has had four defined values since SM1 and a display layer
 * that renders all four, but nothing ever wrote the middle two: on production
 * every live invoice reads OPEN, and whether one is settled can only be
 * answered by computing a balance. That is what made the reconciliation report
 * unusable — a stored status that can disagree with the money is worse than no
 * status at all.
 *
 * So the status is DERIVED, never independently authored: every place that
 * writes an invoice's outstanding balance writes its status in the same breath,
 * from the same two numbers. There is one definition of "settled" and it lives
 * here.
 */

/**
 * Half a cent. Totals and balances are decimals rounded to two places on the
 * way into the database, so anything smaller than this is rounding noise, not
 * money owed. Without it a $0.001 residue would hold an invoice open forever.
 */
export const INVOICE_SETTLEMENT_TOLERANCE = 0.005;

/**
 * The status the money implies, or `null` when the invoice's status must not be
 * touched at all.
 *
 * Returns null for a CANCELLED invoice: cancelling is an operator decision
 * about the document, not a statement about its balance, and a recalculation
 * must never resurrect one.
 *
 * Rules (settled with Chris, 2026-08-02):
 * - Balance zero against a non-zero total → CLOSED.
 * - Part paid → PARTIALLY_CLOSED.
 * - Nothing paid → OPEN.
 * - A zero-total invoice stays OPEN — there is nothing to have closed against,
 *   and calling an empty document "closed" would read as settled revenue.
 *
 * Overpayment is not a status. The balance floors at zero, so an over-collected
 * invoice reads CLOSED and the over-collection is surfaced by the
 * reconciliation report, which is the surface that can also say by how much.
 */
export function deriveInvoiceStatusFromBalance(
	currentStatus: string | null | undefined,
	invoiceTotal: number,
	outstandingBalance: number,
): InvoiceStatus | null {
	if (currentStatus === InvoiceStatus.CANCELLED) {
		return null;
	}

	if (!Number.isFinite(invoiceTotal) || !Number.isFinite(outstandingBalance)) {
		return null;
	}

	if (invoiceTotal <= INVOICE_SETTLEMENT_TOLERANCE) {
		return InvoiceStatus.OPEN;
	}

	if (outstandingBalance <= INVOICE_SETTLEMENT_TOLERANCE) {
		return InvoiceStatus.CLOSED;
	}

	if (outstandingBalance < invoiceTotal - INVOICE_SETTLEMENT_TOLERANCE) {
		return InvoiceStatus.PARTIALLY_CLOSED;
	}

	return InvoiceStatus.OPEN;
}

/**
 * Whether the manual Close action may be offered.
 *
 * Only when the money says the invoice is settled — otherwise Close would be a
 * way to author a status the balance disagrees with, which is the thing this
 * feature exists to remove.
 */
export function canManuallyCloseInvoice(
	currentStatus: string | null | undefined,
	invoiceTotal: number,
	outstandingBalance: number,
): boolean {
	return (
		deriveInvoiceStatusFromBalance(
			currentStatus,
			invoiceTotal,
			outstandingBalance,
		) === InvoiceStatus.CLOSED && currentStatus !== InvoiceStatus.CLOSED
	);
}

/**
 * Whether an invoice is closed in the sense that matters to an operator about
 * to change it: the actions that alter the document (edit, cancel) refuse a
 * closed invoice, so these are the states that need a reopen prompt first.
 */
export function isInvoiceClosed(status: string | null | undefined): boolean {
	return status === InvoiceStatus.CLOSED;
}

/**
 * PD-16381 — the statuses that take an invoice out of every payment surface,
 * whatever balance it carries: CLOSED (settled, as decided by whichever system
 * masters the tenant's invoices — Workd's balance-derived lifecycle above, or
 * the ERP on a tenant whose ERP masters them) and CANCELLED (withdrawn).
 *
 * On a consistent document a closed invoice has no balance, so this and the
 * balance test agree. They part only when the two disagree — an ERP that
 * closed an invoice while Workd's own ledger still shows money on it — and
 * then the status wins: a payment against a document the system of record has
 * closed has nowhere to be applied. The invoice page used to test the balance
 * alone while Make Payment tested both, so such an invoice was payable from
 * one screen and absent from the other.
 */
export const INVOICE_STATUSES_CLOSED_TO_PAYMENT: ReadonlyArray<InvoiceStatus> =
	[InvoiceStatus.CANCELLED, InvoiceStatus.CLOSED];

/** Whether an invoice's status still allows it to be paid (see above). */
export function isInvoiceStatusPayable(
	status: string | null | undefined,
): boolean {
	return !INVOICE_STATUSES_CLOSED_TO_PAYMENT.some(
		(closed) => (closed as string) === status,
	);
}
